import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/esm/ChevronLeft';
import ChevronRight from '@mui/icons-material/esm/ChevronRight';
import { buildMonthGrid } from './supervisorFieldUtils';

interface SupervisorMonthCalendarProps {
  month: Date;
  selectedDate?: string;
  onMonthChange: (month: Date) => void;
  onSelectDate: (date: string) => void;
  renderDay?: (date: string) => ReactNode;
}

const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function SupervisorMonthCalendar({
  month,
  selectedDate,
  onMonthChange,
  onSelectDate,
  renderDay,
}: SupervisorMonthCalendarProps) {
  const days = buildMonthGrid(month);

  const moveMonth = (offset: number) => {
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + offset, 1));
  };

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <IconButton aria-label="Previous month" onClick={() => moveMonth(-1)}>
          <ChevronLeft />
        </IconButton>
        <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
          {month.toLocaleDateString([], { month: 'long', year: 'numeric' })}
        </Typography>
        <IconButton aria-label="Next month" onClick={() => moveMonth(1)}>
          <ChevronRight />
        </IconButton>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
        {weekdays.map((day) => (
          <Typography
            key={day}
            variant="caption"
            align="center"
            sx={{ py: 0.5, color: 'text.secondary', fontWeight: 800 }}
          >
            {day}
          </Typography>
        ))}
        {days.map((day) => {
          const selected = selectedDate === day.date;
          return (
            <Box
              key={day.date}
              component="button"
              type="button"
              aria-label={`Select ${day.date}`}
              aria-pressed={selected}
              onClick={() => onSelectDate(day.date)}
              sx={{
                appearance: 'none',
                minWidth: 0,
                minHeight: { xs: 72, sm: 92 },
                p: { xs: 0.5, sm: 0.75 },
                border: '1px solid',
                borderColor: selected ? '#276749' : day.isToday ? '#d0a500' : '#dfe3df',
                bgcolor: selected ? '#e8f3ec' : day.inMonth ? '#fff' : '#f5f6f5',
                color: day.inMonth ? '#172018' : '#8a928d',
                textAlign: 'left',
                cursor: 'pointer',
                overflow: 'hidden',
                '&:hover': { borderColor: '#6f7d73', bgcolor: selected ? '#e8f3ec' : '#f8faf8' },
                '&:focus-visible': { outline: '3px solid #8fb99d', outlineOffset: 1 },
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: day.isToday || selected ? 900 : 700 }}>
                {day.dayNumber}
              </Typography>
              <Box sx={{ mt: 0.25, minWidth: 0 }}>{renderDay?.(day.date)}</Box>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}
